/**
 * Admin · speech quality (GET /admin/speech-checks) — the ASR harness console.
 *
 * Everything the judge scores sits on the browser Web Speech transcript, and judge fairness was
 * only ever measured on clean text. Volunteers read known passages at /speech-check on their own
 * phones; this screen turns those rows into the go/no-go number for the pilot. The volunteer is
 * NEVER the subject here — every mark on this screen is a verdict on OUR transcription, and the
 * copy says so.
 *
 * The expandable diff is a deliberate bag-of-words mark, not an alignment render: a token is
 * marked when the other side has no copy of it anywhere. That is enough to SEE what was misheard
 * ("btech" for "b tech") without a diff library; the exact S/D/I counts next to it come from the
 * server's Levenshtein backtrace, which is the number that decides.
 */
import { useState, type CSSProperties, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { normaliseSpeech } from "@talenthop/shared";
import { api } from "../../lib/api.js";

interface WerAgg {
  n: number;
  meanWer: number;
  p90Wer: number;
}

interface CheckRow {
  id: string;
  tag: string;
  passageId: string;
  reference: string | null;
  transcript: string;
  wer: number | null;
  substitutions: number | null;
  deletions: number | null;
  insertions: number | null;
  refTokens: number | null;
  confidenceAvg: number | null;
  restarts: number;
  durationMs: number;
  device: { ua?: string | null; browserFamily?: string; platform?: string; label?: string | null };
  accentNote: string | null;
  createdAt: string;
}

interface SpeechChecksResponse {
  rows: CheckRow[];
  aggregates: {
    overall: WerAgg;
    byPassage: ({ passageId: string } & WerAgg)[];
    byBrowserFamily: ({ family: string } & WerAgg)[];
  };
}

const pct = (wer: number): string => `${(wer * 100).toFixed(1)}%`;

/** The decision rule's colour bands: <15% sound, 15–25% caveat, >25% fix first. */
const werTone = (wer: number): string =>
  wer < 0.15 ? "var(--good)" : wer <= 0.25 ? "var(--warn)" : "var(--crit)";

/**
 * Mark each of `own`'s scored tokens that has no counterpart left in `other` (bag semantics —
 * duplicates are consumed one-for-one so "the the" vs "the" marks exactly one).
 */
function markTokens(own: string, other: string): { tok: string; miss: boolean }[] {
  const ownToks = normaliseSpeech(own).split(" ").filter(Boolean);
  const budget = new Map<string, number>();
  for (const t of normaliseSpeech(other).split(" ").filter(Boolean)) {
    budget.set(t, (budget.get(t) ?? 0) + 1);
  }
  return ownToks.map((tok) => {
    const left = budget.get(tok) ?? 0;
    if (left > 0) {
      budget.set(tok, left - 1);
      return { tok, miss: false };
    }
    return { tok, miss: true };
  });
}

const markStyle: CSSProperties = {
  background: "var(--crit-wash)",
  color: "var(--crit)",
  borderRadius: 3,
  padding: "0 2px",
};

function MarkedText({ own, other }: { own: string; other: string }): ReactNode {
  return (
    <span style={{ lineHeight: 1.7 }}>
      {markTokens(own, other).map((t, i) => (
        <span key={i}>
          {t.miss ? <span style={markStyle}>{t.tok}</span> : t.tok}{" "}
        </span>
      ))}
    </span>
  );
}

function AggTable({
  title,
  nameHeader,
  rows,
}: {
  title: string;
  nameHeader: string;
  rows: ({ name: string } & WerAgg)[];
}): ReactNode {
  return (
    <div className="card">
      <div className="card-h">
        <h3>{title}</h3>
        <span className="tag">reference-bearing rows only</span>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>{nameHeader}</th>
              <th>Checks</th>
              <th>Mean WER</th>
              <th>p90 WER</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="mut sm">
                  Nothing scored yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.name}>
                <td>{r.name}</td>
                <td className="tnum">{r.n}</td>
                <td className="tnum" style={{ color: werTone(r.meanWer), fontWeight: 650 }}>
                  {pct(r.meanWer)}
                </td>
                <td className="tnum" style={{ color: werTone(r.p90Wer), fontWeight: 650 }}>
                  {pct(r.p90Wer)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SpeechQuality(): ReactNode {
  const [open, setOpen] = useState<string | null>(null);
  // Tag = the wave separator the shared link carries (?tag=pilot1). Filtering here is what keeps
  // wave 2, strays and test submissions out of pilot 1's go/no-go headline.
  const [tag, setTag] = useState("");
  const cleanTag = /^[a-z0-9-]{1,40}$/.test(tag.trim()) ? tag.trim() : "";
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-speech-checks", cleanTag],
    queryFn: () =>
      api.get<SpeechChecksResponse>(`/admin/speech-checks${cleanTag ? `?tag=${cleanTag}` : ""}`),
  });

  const rows = data?.rows ?? [];
  const agg = data?.aggregates;
  const shareUrl = `${window.location.origin}/speech-check`;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Speech quality</h1>
          <input
            className="rq-in"
            style={{ maxWidth: 220, marginTop: 8 }}
            placeholder="Filter by wave tag (e.g. pilot1)"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
          />
          <div className="sub">
            Does Indian English on real phones survive our transcription? · text only, no audio ever stored
          </div>
        </div>
        <span className="pill n">WER recomputed server-side · volunteers are never judged</span>
      </div>

      {/* The decision rule IS the point of this screen — visible, not tribal knowledge. */}
      <div className="banner" style={{ marginBottom: 16 }}>
        <span>
          <b>Decision rule:</b> mean WER <b style={{ color: "var(--good)" }}>&lt;15%</b> = transcription is
          sound · <b style={{ color: "var(--warn)" }}>15–25%</b> = usable, surface a confidence caveat to
          reviewers · <b style={{ color: "var(--crit)" }}>&gt;25% for any accent group</b> = fix transcription
          before the pilot.
        </span>
      </div>

      {isLoading && <div className="card pad mut sm">Loading speech checks…</div>}
      {isError && (
        <div className="banner crit">
          <span>Couldn't load speech checks. Please retry in a moment.</span>
        </div>
      )}

      {data && rows.length === 0 && (
        <div className="card pad">
          <div style={{ fontWeight: 650, marginBottom: 6 }}>No checks yet — share the link</div>
          <div className="mut sm" style={{ marginBottom: 8 }}>
            Send this to volunteers; it works on their own phone, needs no account, and records text only:
          </div>
          <div style={{ fontFamily: "var(--mono)", fontSize: 13 }}>{shareUrl}</div>
        </div>
      )}

      {data && rows.length > 0 && agg && (
        <>
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">Checks run</div>
              <div className="n violet tnum">{rows.length >= 500 ? "500+" : rows.length}</div>
              <div className="d">{agg.overall.n} scored against a reference</div>
            </div>
            <div className="card pad stat">
              <div className="l">Mean WER</div>
              <div className="n tnum" style={{ color: werTone(agg.overall.meanWer) }}>
                {pct(agg.overall.meanWer)}
              </div>
              <div className="d">across all reference passages</div>
            </div>
            <div className="card pad stat">
              <div className="l">p90 WER</div>
              <div className="n tnum" style={{ color: werTone(agg.overall.p90Wer) }}>
                {pct(agg.overall.p90Wer)}
              </div>
              <div className="d">nearest-rank — a score someone actually got</div>
            </div>
            <div className="card pad stat">
              <div className="l">Share link</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 12.5, wordBreak: "break-all" }}>{shareUrl}</div>
              <div className="d">no account · text only</div>
            </div>
          </div>

          <div className="grid g2" style={{ marginBottom: 16 }}>
            <AggTable
              title="By browser"
              nameHeader="Browser family"
              rows={agg.byBrowserFamily.map(({ family, ...a }) => ({ name: family, ...a }))}
            />
            <AggTable
              title="By passage"
              nameHeader="Passage"
              rows={agg.byPassage.map(({ passageId, ...a }) => ({ name: passageId, ...a }))}
            />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-h">
              <h3>Checks</h3>
              <span className="tag">newest first · click a row for the transcript diff</span>
            </div>
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Tag</th>
                    <th>Passage</th>
                    <th>Browser</th>
                    <th>WER</th>
                    <th>Restarts</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <RowPair key={r.id} row={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="disclaimer">
        <b>What this measures:</b> our transcription pipeline, never the volunteer. References and transcripts
        are compared after normalisation (case, punctuation, digits-vs-words, %/₹ spellings), so a correctly
        heard "20 percent" for "20%" scores zero error. Unfoldable ambiguity still counts as an error — every
        WER here is an upper bound on the engine's true error, never flattered.
      </div>
    </section>
  );
}

function RowPair({ row, open, onToggle }: { row: CheckRow; open: boolean; onToggle: () => void }): ReactNode {
  const browser = row.device.browserFamily ?? "Unknown";
  const platform = row.device.platform ?? "";
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }}>
        <td className="mut sm">{new Date(row.createdAt).toLocaleString()}</td>
        <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{row.tag}</td>
        <td>{row.passageId}</td>
        <td>
          {browser}
          {platform ? <span className="mut sm"> · {platform}</span> : null}
        </td>
        <td className="tnum" style={row.wer !== null ? { color: werTone(row.wer), fontWeight: 650 } : undefined}>
          {row.wer !== null ? pct(row.wer) : "—"}
        </td>
        <td className="tnum">{row.restarts}</td>
        <td className="tnum">{row.confidenceAvg !== null ? row.confidenceAvg.toFixed(2) : "—"}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ background: "var(--surface-2)" }}>
            <div style={{ padding: "10px 4px", display: "grid", gap: 10 }}>
              {row.reference !== null ? (
                <>
                  <div>
                    <div className="mut sm" style={{ marginBottom: 4 }}>
                      Reference (as scored) — <span style={markStyle}>marked</span> words were substituted or missed:
                    </div>
                    <MarkedText own={row.reference} other={row.transcript} />
                  </div>
                  <div>
                    <div className="mut sm" style={{ marginBottom: 4 }}>
                      Transcript (as scored) — <span style={markStyle}>marked</span> words were misheard or inserted:
                    </div>
                    <MarkedText own={row.transcript} other={row.reference} />
                  </div>
                  <div className="mut sm">
                    {row.substitutions} substituted · {row.deletions} missed · {row.insertions} inserted · over{" "}
                    {row.refTokens} reference words · {(row.durationMs / 1000).toFixed(0)}s of speech
                  </div>
                </>
              ) : (
                <div>
                  <div className="mut sm" style={{ marginBottom: 4 }}>
                    Free speech (no reference — transcript stored as heard, no WER computable):
                  </div>
                  <span style={{ lineHeight: 1.7 }}>{row.transcript}</span>
                </div>
              )}
              {(row.accentNote || row.device.label) && (
                <div className="mut sm">
                  {row.accentNote ? <>Self-described accent: {row.accentNote}</> : null}
                  {row.accentNote && row.device.label ? " · " : null}
                  {row.device.label ? <>Device: {row.device.label}</> : null}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
